import { useEffect, useState } from "react";
import { BRAND, BRAND_DARK } from "@/shared/constants.js";
import { useStore } from "@/shared/store/useStore.js";
import { useIlfDlfResponse } from "@/hooks/useIlfDlfFetcher.js";
import submissionStore from "@/modules/Quote/store/submissionStore.js";
import coverageStore from "@/modules/Quote/store/coverageStore.js";
import paymentOrderStore from "@/modules/Payment/store/paymentOrderStore.js";

// All strip amounts render with cents (2dp) per design.
const fmt2 = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Breakdown line items for the overlay, derived from whichever rating source
// is currently authoritative. Mirrors the quoteStripAnnualTotal precedence in
// derivedValues.js so the itemization matches the number on the strip:
//   - steps <= 3: the live ILF/DLF estimate wins (itemized fee/tax arrays);
//   - steps >= 4: a fresh calculate-rating result wins (also itemized), then
//     the saved order's rating (scalar fees/tax → one row each), then ILF.
// `estimate: true` marks soft-quote sources so the overlay can show the
// "estimate only" disclaimer just for them.
function useBreakdownSource() {
  const step = useStore(submissionStore, (s) => s.step);
  const ilf = useIlfDlfResponse();
  const calcR = useStore(coverageStore, (s) => s.calculateRatingResult);
  const ratingR = useStore(paymentOrderStore, (s) => s.paymentOrderDetails)?.ratingResponse;

  const fromIlf = ilf?.premium != null ? {
    premium: Number(ilf.premium) || 0,
    feeRows: Array.isArray(ilf.fees) ? ilf.fees : [],
    taxRows: Array.isArray(ilf.taxes) ? ilf.taxes : [],
    estimate: true,
  } : null;
  const fromCalc = calcR?.premium != null ? {
    premium: Number(calcR.premium) || 0,
    feeRows: Array.isArray(calcR.fees) ? calcR.fees : [],
    taxRows: Array.isArray(calcR.taxes) ? calcR.taxes : [],
    estimate: false,
  } : null;
  const fromOrder = (ratingR && (ratingR.premium != null || ratingR.tax != null || ratingR.fees != null)) ? {
    premium: Number(ratingR.premium) || 0,
    feeRows: ratingR.fees != null ? [{ label: "Fees", value: ratingR.fees }] : [],
    taxRows: ratingR.tax != null ? [{ label: "Taxes", value: ratingR.tax }] : [],
    estimate: false,
  } : null;

  return step <= 3
    ? (fromIlf ?? fromCalc ?? fromOrder)
    : (fromCalc ?? fromOrder ?? fromIlf);
}

export const QuoteStrip = ({
  total, limits, claims = "Claims-made", amountPending = false,
  amountLabel = "Your quote", pendingLabel = "Loading…", style,
}) => {
  const [showBreakdown, setShowBreakdown] = useState(false);
  const breakdown = useBreakdownSource();
  const tappable = !amountPending && Number(total) > 0 && breakdown != null;

  return (
    <>
      <div
        role={tappable ? "button" : undefined}
        tabIndex={tappable ? 0 : undefined}
        onClick={() => { if (tappable) setShowBreakdown(true); }}
        onKeyDown={(e) => { if (tappable && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setShowBreakdown(true); } }}
        style={{
          background: "#f7f7f5", borderRadius: 10, padding: "10px 14px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginBottom: 10, cursor: tappable ? "pointer" : "default",
          ...(style || {}),
        }}
      >
        <div>
          <div style={{ fontSize: 10, color: "#999" }}>{amountLabel}</div>
          {/* 26px matches the desktop rail's Est. premium total (QuoteSnapshotRail
              premValue) so the amount reads the same size on both surfaces. */}
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 26, fontWeight: 700, color: BRAND_DARK, minHeight: 34, display: "flex", alignItems: "center" }}>
            {amountPending ? (
              <span style={{ fontSize: 16, fontWeight: 500, color: "#aaa", fontFamily: "'DM Sans', sans-serif" }}>{pendingLabel}</span>
            ) : (
              <>
                ${fmt2(total)}<span style={{ fontSize: 11, color: "#999", fontFamily: "'DM Sans', sans-serif", fontWeight: 400, marginLeft: 4 }}>/yr</span>
              </>
            )}
          </div>
          {/* Claims-made line sits below the amount (not on the right) per design. */}
          {!amountPending && claims ? (
            <div style={{ fontSize: 11, color: "#999", marginTop: 2, fontFamily: "'DM Sans', sans-serif" }}>{claims}</div>
          ) : null}
        </div>
        <div style={{ fontSize: 11, color: "#999", textAlign: "right", lineHeight: 1.5, fontFamily: "'DM Sans', sans-serif" }}>
          <div>{limits}</div>
          {tappable && (
            <div style={{ fontWeight: 500, color: BRAND, marginTop: 2 }}>View breakdown</div>
          )}
        </div>
      </div>
      {showBreakdown && (
        <EstimateBreakdownOverlay
          title={amountLabel}
          premium={breakdown.premium}
          feeRows={breakdown.feeRows}
          taxRows={breakdown.taxRows}
          showEstimateNote={breakdown.estimate}
          total={total}
          limits={limits}
          policyLine={claims}
          onClose={() => setShowBreakdown(false)}
        />
      )}
    </>
  );
};

// Full-screen breakdown, opened by tapping the strip. A prominent overlay
// (dark scrim + large centered panel) rather than a small anchored popup, so
// the itemization is readable on mobile. Escape or the scrim/X closes it;
// page scroll locks while open.
function EstimateBreakdownOverlay({ title, premium, feeRows, taxRows, showEstimateNote, total, limits, policyLine, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  // Fees stay itemized as returned; the carrier's tax line items are
  // combined into ONE "Taxes" row (display-only sum).
  const taxTotal = taxRows.reduce((sum, t) => sum + (Number(t?.value) || 0), 0);

  const row = (key, label, value, muted) => (
    <div key={key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "7px 0" }}>
      <span style={{ fontSize: 13, color: "#666" }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 500, color: muted ? "#999" : "#333", whiteSpace: "nowrap" }}>{value}</span>
    </div>
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${title} breakdown`}
      style={{ position: "fixed", inset: 0, zIndex: 1300, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
    >
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(26,26,26,0.55)" }} />
      <div style={{
        position: "relative", width: "min(560px, 100%)", maxHeight: "92vh", overflowY: "auto",
        background: "#fff", borderRadius: 14, boxShadow: "0 18px 50px rgba(0,0,0,0.28)", padding: "22px 24px",
      }}>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close breakdown"
          style={{ position: "absolute", top: 14, right: 14, background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex", color: "#888" }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: 18, height: 18 }}>
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div style={{ fontSize: 10, color: "#999", textTransform: "uppercase", letterSpacing: "0.06em" }}>{title}</div>
        <div style={{ fontFamily: "'Fraunces', serif", fontSize: 32, fontWeight: 600, color: BRAND_DARK, margin: "4px 0 2px" }}>
          ${fmt2(total)}<span style={{ fontSize: 13, color: "#999", fontFamily: "'DM Sans', sans-serif", fontWeight: 400, marginLeft: 4 }}>/yr</span>
        </div>
        {/* Limits and the claims-made policy line each get their own line. */}
        <div style={{ fontSize: 12, color: "#999", marginBottom: 14 }}>
          {limits ? <div>{limits}</div> : null}
          {policyLine ? <div>{policyLine}</div> : null}
        </div>

        <div style={{ borderTop: "1px solid #eee", paddingTop: 10 }}>
          {row("premium", "Annual premium", premium > 0 ? `$${fmt2(premium)}` : "—")}
          {feeRows.map((f, i) => row(`fee-${i}`, f?.label || "Fee", `$${fmt2(f?.value)}`))}
          {feeRows.length === 0 && row("fees-empty", "Fees", "—", true)}
          {row("taxes", "Taxes", taxTotal > 0 ? `$${fmt2(taxTotal)}` : "—", taxTotal <= 0)}
        </div>

        <div style={{ height: 1, background: "#e0e0de", margin: "8px 0" }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 14, fontWeight: 500, color: "#333" }}>Annual total</span>
          <span style={{ fontSize: 15, fontWeight: 500, color: BRAND_DARK }}>${fmt2(total)}</span>
        </div>

        {showEstimateNote && (
          <div style={{ fontSize: 11, color: "#aaa", lineHeight: 1.5, marginTop: 12 }}>
            Estimate based on standard rates for your ZIP. Your exact total is confirmed before payment — no surprises at checkout.
          </div>
        )}
      </div>
    </div>
  );
}
